import React from 'react';
import { Grid, Paper, Box, Typography, LinearProgress, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloudIcon from '@mui/icons-material/Cloud';
import TranslateIcon from '@mui/icons-material/Translate';

import { DASHBOARD_COLORS, formatBytes } from './dashboardTheme';

const UNLIMITED = 9007199254740991;

function UsageBar({ used, limit, percent, color }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                    flexGrow: 1,
                    mr: 2,
                    height: 8,
                    borderRadius: 4,
                    '& .MuiLinearProgress-bar': { bgcolor: color },
                }}
            />
            <Typography variant="body2" sx={{ minWidth: 56, textAlign: 'right' }}>
                {limit >= UNLIMITED ? 'Unlimited' : `${percent.toFixed(1)}%`}
            </Typography>
        </Box>
    );
}

function UsageCard({ title, icon, used, limit, formatter, locked, lockedMessage, ctaLabel, ctaIcon, onCta }) {
    const percent = limit >= UNLIMITED ? 100 : Math.min((used / limit) * 100, 100);
    const overEighty = percent >= 80 && limit < UNLIMITED;
    const accent = overEighty ? DASHBOARD_COLORS.warning : DASHBOARD_COLORS.progress;

    return (
        <Paper
            sx={{
                p: 3,
                borderRadius: '16px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 160,
                bgcolor: locked ? '#f8f9fa' : '#fff',
            }}
        >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                {locked ? <LockIcon color="disabled" /> : icon}
                <Typography variant="h6" color={locked ? 'text.secondary' : 'text.primary'}>
                    {title}
                </Typography>
            </Box>

            {locked ? (
                <>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {lockedMessage}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onCta}
                        startIcon={ctaIcon}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        {ctaLabel}
                    </Button>
                </>
            ) : (
                <>
                    <Typography variant="body2" color="text.secondary">
                        {formatter(used)} of {limit >= UNLIMITED ? 'Unlimited' : formatter(limit)}
                    </Typography>
                    <UsageBar used={used} limit={limit} percent={percent} color={accent} />
                    {overEighty && (
                        <Typography variant="caption" sx={{ mt: 1, color: DASHBOARD_COLORS.warning }}>
                            You're approaching your limit.
                        </Typography>
                    )}
                </>
            )}
        </Paper>
    );
}

export default function UsageCards({ charUsed, charLimit, storageUsed, storageLimit }) {
    const navigate = useNavigate();
    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
                <UsageCard
                    title="Machine Translation"
                    icon={<TranslateIcon color="primary" />}
                    used={charUsed}
                    limit={charLimit}
                    formatter={(n) => `${n.toLocaleString()} characters`}
                    locked={charLimit === 0}
                    lockedMessage="MT characters are not included in your current plan. Upgrade to start using automated translations."
                    ctaLabel="Upgrade Plan"
                    ctaIcon={<TrendingUpIcon />}
                    onCta={() => navigate('/dashboard/pricing')}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <UsageCard
                    title="Storage"
                    icon={<CloudIcon color="primary" />}
                    used={storageUsed}
                    limit={storageLimit}
                    formatter={formatBytes}
                    locked={storageLimit === 0}
                    lockedMessage="Storage is not included in your current plan. Upgrade to upload and manage project files."
                    ctaLabel="Manage Files"
                    ctaIcon={<CloudIcon />}
                    onCta={() => navigate('/dashboard/files')}
                />
            </Grid>
        </Grid>
    );
}
